import React, {ReactNode, useLayoutEffect, useState} from "react";
import {styled} from "@mui/material/styles";
import {Badge, BadgeProps, Switch} from "@mui/material";
import {strEmpty} from "./utils.ts";
import {FilterAltOff, KeyboardArrowDown, KeyboardArrowUp} from "@mui/icons-material";
import {Maybe} from "../graphql/generated/graphql-main.tsx";

/**
 * Given nullable value, if it's non-null, pass it to a function that returns any ReactNode (JSX Element);
 * used, for instance, to conditionally display property icons if the value is available for that property
 * @param val
 * @param func
 */
export const nodeIf = (val?: Maybe<string>, func?: (val: string) => React.ReactNode) => {
    if (val == null || strEmpty(val)) return null;
    return func?.(val);
}

/**
 * Proper icon to show based on order of sorting
 * @param direction
 */
export const sortIcon = (direction?: number | null) => {
    // const icon = <Sort/>;
    const icon = <FilterAltOff/>;
    if (direction == 1) // asc
        return <KeyboardArrowUp/>
    else if (direction == 0) // desc
        return <KeyboardArrowDown/>

    return <span className={"ml-2 flex items-center"}>{icon}</span>
}

/**
 * todo preferable to use [ViewportProvider] for listening to window size changes and apply responsiveness accordingly
 * Custom hook to detect change of window size in order to effect custom operations (e.g. re-render)
 * when window size changes (browser window resize)
 *
 * https://stackoverflow.com/questions/19014250/rerender-view-on-browser-resize-with-react
 */
export function useWindowSize() {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }
        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
}

/**
 * Custom styling of default MUI [Switch] component to look more like Android switches
 * @source
 *  - https://mui.com/material-ui/react-switch/#customization
 */
export const AndroidSwitch = styled(Switch)(() => ({
    padding: 8,
    '& .MuiSwitch-track': {
        borderRadius: 22 / 2,
        '&:before, &:after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 16,
            height: 16,
        },
        '&:before': {
            // TODO custom img background is NOT needed
            // backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
            //     theme.palette.getContrastText(theme.palette.primary.main),
            // )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
            left: 12,
        },
        '&:after': {
            // TODO custom img background is NOT needed
            // backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
            //     theme.palette.getContrastText(theme.palette.primary.main),
            // )}" d="M19,13H5V11H19V13Z" /></svg>')`,
            right: 12,
        },
    },
    '& .MuiSwitch-thumb': {
        boxShadow: 'none',
        width: 16,
        height: 16,
        margin: 2,
    },
}));

/**
 * Custom-styled [Badge] component
 * @param props
 * @constructor
 */
export const StyledBadge = (props: {
    content: string|number,
    badge?: BadgeProps,
    children: ReactNode
}) => {
    return <Badge
        anchorOrigin={{
            horizontal: "left",
            vertical: "top",
            ...props.badge?.anchorOrigin
        }}
        classes={{
            root: "w-full",
            badge: "bg-jaffa bg-opacity-50 -left-2 top-3 z-0",
            ...props.badge?.classes
        }}
        badgeContent={props.content ?? props.badge?.badgeContent}
    >
        {props.children}
    </Badge>
}
